import { Button, Skeleton, cn } from '@shigo-ui/ui';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';

import { dashboardT } from '../../../../lib/messages';
import { TargetingLocationType, type TargetingLocation } from '../../model';
import { formatGeoTypeLabel, geoLocationKey, type GeoNode } from './GeolocationTargeting.helpers';

type GeoNodeRowProps = {
  expandedIds: string[];
  expandable?: boolean;
  description?: string;
  node: GeoNode;
  onSelect: (node: GeoNode, excluded: boolean) => void;
  onToggleExpanded: (node: GeoNode) => void;
  selectedLocations: TargetingLocation[];
};

export function GeoNodeRow({
  expandedIds,
  expandable = true,
  description,
  node,
  onSelect,
  onToggleExpanded,
  selectedLocations,
}: GeoNodeRowProps) {
  const expanded = expandedIds.includes(node.id);
  const hasChildren = node.type !== TargetingLocationType.City;
  const selectedLocation = selectedLocations.find(
    (location) => geoLocationKey(location) === node.id,
  );
  const includeActive = selectedLocation ? !selectedLocation.excluded : false;
  const excludeActive = selectedLocation?.excluded === true;

  return (
    <div className="grid gap-1">
      <div className="hover:bg-muted/40 flex items-center gap-2 rounded-sm px-2 py-1">
        {expandable ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={!hasChildren}
            aria-label={dashboardT(expanded ? 'targeting.collapseNamed' : 'targeting.expandNamed', {
              name: node.label,
            })}
            onClick={() => {
              onToggleExpanded(node);
            }}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        ) : (
          <span className="h-7 w-7 shrink-0" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-foreground truncate text-sm font-medium">{node.label}</div>
          <div className="text-muted-foreground truncate text-xs">
            {description ?? formatGeoTypeLabel(node.type)}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            'h-8 w-8 rounded-full',
            includeActive && 'border-primary bg-primary/10 text-primary',
          )}
          aria-label={dashboardT('targeting.includeNamed', { name: node.label })}
          aria-pressed={includeActive}
          onClick={() => {
            onSelect(node, false);
          }}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            'h-8 w-8 rounded-full',
            excludeActive && 'border-destructive bg-destructive/10 text-destructive',
          )}
          aria-label={dashboardT('targeting.excludeNamed', { name: node.label })}
          aria-pressed={excludeActive}
          onClick={() => {
            onSelect(node, true);
          }}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {expanded ? (
        <div className="border-border ml-5 grid gap-1 border-l pl-3">
          {node.loading ? <Skeleton className="h-8" /> : null}
          {node.children?.map((child) => (
            <GeoNodeRow
              key={child.id}
              expandedIds={expandedIds}
              node={child}
              onSelect={onSelect}
              onToggleExpanded={onToggleExpanded}
              selectedLocations={selectedLocations}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
